import React, { useCallback, useEffect, useState } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Checkbox,
  Button,
} from '@roger/ui-components';
import { ChevronDown, Loader2 } from 'lucide-react';
import { client } from '../services/api';
import type { UserResponse } from '../services/api';
import { filterAssigneeUsers } from '../constants/assigneeFilters';
import { FeatureFlag } from '../constants/featureFlags';
import { useStore } from '../store';
import { fetchIntakeAssignableUsers } from '../services/intakeAssignableUsers';

export interface AssigneeFilterValue {
  userIds: string[];
  includeUnassigned: boolean;
}

interface IntakeAssigneeFilterProps {
  value: AssigneeFilterValue;
  onChange: (value: AssigneeFilterValue) => void;
}

function displayName(user: UserResponse): string {
  const first = (user.first_name ?? '').trim();
  const last = (user.last_name ?? '').trim();
  if (first || last) return `${first} ${last}`.trim();
  return user.email ?? user.id ?? 'Unknown';
}

const IntakeAssigneeFilter: React.FC<IntakeAssigneeFilterProps> = ({
  value,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const hasFeatureFlag = useStore(state => state.hasFeatureFlag);
  const useRogerRbac = hasFeatureFlag(FeatureFlag.ROGER_RBAC);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const res = await fetchIntakeAssignableUsers(client, useRogerRbac);
      setUsers(filterAssigneeUsers(res.data ?? []));
    } catch {
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [useRogerRbac]);

  useEffect(() => {
    if (open) {
      void loadUsers();
    }
  }, [open, loadUsers]);

  const hasFilter =
    value.includeUnassigned || (value.userIds && value.userIds.length > 0);

  const toggleUnassigned = () => {
    onChange({
      ...value,
      includeUnassigned: !value.includeUnassigned,
    });
  };

  const toggleUser = (userId: string) => {
    const current = value.userIds ?? [];
    const next = current.includes(userId)
      ? current.filter(id => id !== userId)
      : [...current, userId];
    onChange({ ...value, userIds: next });
  };

  const clearFilter = () => {
    onChange({ userIds: [], includeUnassigned: false });
    setOpen(false);
  };

  const getDisplayText = () => {
    if (!hasFilter) return 'All Assignees';
    const parts: string[] = [];
    if (value.includeUnassigned) parts.push('Unassigned');
    const count = value.userIds?.length ?? 0;
    if (count > 0) {
      if (count === 1 && users.length) {
        const u = users.find(x => x.id === value.userIds![0]);
        parts.push(u ? displayName(u) : '1 assignee');
      } else {
        parts.push(`${count} assignees`);
      }
    }
    return parts.join(', ');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          size='default'
          className='h-9 justify-between min-w-0 max-w-full w-full font-normal overflow-hidden'
        >
          <span className='truncate min-w-0'>{getDisplayText()}</span>
          <ChevronDown className='h-4 w-4 opacity-50 ml-2 flex-shrink-0' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-56 p-0' align='end' side='bottom'>
        <div className='p-1 max-h-[280px] overflow-y-auto'>
          {/* Unassigned */}
          <button
            type='button'
            onClick={toggleUnassigned}
            className='w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm hover:bg-muted transition-colors text-left'
          >
            <Checkbox
              checked={value.includeUnassigned}
              onCheckedChange={toggleUnassigned}
              className='pointer-events-none'
            />
            Unassigned
          </button>

          {/* Clear when something selected */}
          {hasFilter && (
            <button
              type='button'
              onClick={clearFilter}
              className='w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm hover:bg-muted transition-colors text-left text-muted-foreground'
            >
              Clear filter
            </button>
          )}

          {/* User list */}
          <div className='border-t border-border mt-1 pt-1'>
            {usersLoading ? (
              <div className='flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground'>
                <Loader2 className='h-4 w-4 animate-spin' />
                Loading users…
              </div>
            ) : (
              users.map(user => {
                const checked = (value.userIds ?? []).includes(user.id);
                return (
                  <button
                    key={user.id}
                    type='button'
                    onClick={() => toggleUser(user.id)}
                    className='w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm hover:bg-muted transition-colors text-left'
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleUser(user.id)}
                      className='pointer-events-none'
                    />
                    <span className='truncate'>{displayName(user)}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default IntakeAssigneeFilter;
